import React, { useEffect, useMemo, useState } from 'react';
import s from '../DietPlanBuilder.module.css';
import { Icon, Toggle } from '../ui';
import { MacroFoodIcon } from '../MacroFoodIcon';
import { MACRO_META } from './goalsStep.shared';
import { InlineFoodSearch } from './InlineFoodSearch';
import { KairaSuggestions } from './KairaSuggestions';
import { copy } from '../../copy';
import { fmtG, fmtInt } from '../../lib/format';
import { kairaKey } from '../../hooks/useKaira';
import { check, gramsPerDay, mealTargets, MIN_G, TOL, totals } from '../../lib/nutrition';
import type { TargetEdit } from '../../state/builderReducer';
import type { Builder, BuilderDerived, Dispatch } from '../../hooks/useDietPlanBuilder';
import {
  TIP_DETAIL_MAX_LEN,
  TIP_HEADING_MAX_LEN,
  type KairaRequest,
  type MacroKey,
  type Patient,
  type Plan,
  type StatusInfo,
} from '../../types';

type MacroKeyEval = 'k' | MacroKey;

const EVAL_KEYS: { k: MacroKeyEval; label: string; unit: string }[] = [
  { k: 'k', label: 'Calories', unit: 'kcal' },
  { k: 'p', label: 'Protein', unit: 'g' },
  { k: 'c', label: 'Carbs', unit: 'g' },
  { k: 'f', label: 'Fats', unit: 'g' },
];

function band(target: number, grams: boolean) {
  if (!(target > 0)) return { low: 0, high: 0 };
  const w = grams ? Math.max(target * TOL, MIN_G) : target * TOL;
  return { low: target - w, high: target + w };
}

function barLayout(actual: number, target: number, grams: boolean) {
  const { low, high } = band(target, grams);
  const max = Math.max(high * 1.2, actual * 1.08, target * 1.2, 1);
  const pct = (v: number) => Math.min(100, Math.max(0, (v / max) * 100));
  return { fill: pct(actual), bandLeft: pct(low), bandWidth: Math.max(0, pct(high) - pct(low)), tick: pct(target) };
}

function macroStatusLabel(c: ReturnType<typeof check>) {
  if (c === 'ok') return copy.meals.onTarget;
  if (c === 'none') return '—';
  return c === 'low' ? copy.meals.tooLow : copy.meals.tooHigh;
}

function metricSubline(
  c: ReturnType<typeof check>,
  actual: number,
  target: number,
  unit: string,
  k: MacroKeyEval,
) {
  if (c === 'ok') return copy.meals.withinRange;
  if (c === 'none') return '—';
  const diff = Math.abs(actual - target);
  const val = k === 'k' ? fmtInt(diff) : fmtG(diff);
  return c === 'high' ? copy.meals.amountOver(val, unit) : copy.meals.amountUnder(val, unit);
}

function mealNavMeta(T: { k: number }, opts: StatusInfo[]) {
  if (!(T.k > 0)) return { dot: 'var(--gf-border)', sub: copy.meals.noTarget, subColor: 'var(--gf-faint)', kcal: '—' };
  if (opts.length && opts.every(o => o.st === 'empty')) {
    return { dot: 'var(--gf-border)', sub: copy.meals.noFoodsYet, subColor: 'var(--gf-muted)', kcal: `${fmtInt(T.k)} kcal` };
  }
  const off = opts.filter(o => o.st !== 'ok').length;
  if (!off) return { dot: 'var(--gf-green)', sub: copy.meals.onTargetStatus, subColor: '#027a48', kcal: `${fmtInt(T.k)} kcal` };
  return {
    dot: 'var(--gf-red-ink)',
    sub: copy.meals.optionOff(off),
    subColor: 'var(--gf-red-ink)',
    kcal: `${fmtInt(T.k)} kcal`,
  };
}

function MetricMark({ k }: { k: MacroKeyEval }) {
  if (k === 'k') {
    return <span className={s.evalMetricSwatch} style={{ background: 'var(--gf-faint)' }} />;
  }
  return (
    <span style={{ color: MACRO_META[k].bg, display: 'inline-flex' }}>
      <MacroFoodIcon k={k} size={12} />
    </span>
  );
}

function EvalMetricCard({ k, label, unit, actual, target }: { k: MacroKeyEval; label: string; unit: string; actual: number; target: number }) {
  const grams = k !== 'k';
  const c = check(actual, target, grams);
  const bar = barLayout(actual, target, grams);

  return (
    <div className={s.evalMetricCard} data-status={c}>
      <div className={s.evalMetricHead}>
        <span className={s.evalMetricLabel}>
          <MetricMark k={k} />
          {label}
        </span>
        {target > 0 && c !== 'none' && (
          <span className={s.evalMetricBadge} data-status={c}>{macroStatusLabel(c)}</span>
        )}
      </div>
      <div className={s.evalMetricValues}>
        <span className={s.evalMetricActual}>{k === 'k' ? fmtInt(actual) : fmtG(actual)}</span>
        <span className={s.evalMetricTarget}>
          / {k === 'k' ? fmtInt(target) : fmtG(target)} {unit}
        </span>
      </div>
      {target > 0 && (
        <>
          <div className={s.evalBar}>
            <span className={s.evalBarBand} style={{ left: `${bar.bandLeft}%`, width: `${bar.bandWidth}%` }} />
            <span className={s.evalBarFill} style={{ width: `${bar.fill}%` }} />
            <span className={s.evalBarTick} style={{ left: `calc(${bar.tick}% - 1px)` }} />
          </div>
          <span className={s.evalMetricSub}>{metricSubline(c, actual, target, unit, k)}</span>
        </>
      )}
    </div>
  );
}

function TrashBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" className={s.mealsTrashBtn} onClick={onClick} aria-label={label}>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
        <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
      </svg>
    </button>
  );
}

export function MealsStepDesktop({
  plan,
  patient,
  derived,
  mi,
  oi,
  targetEdit,
  dispatch,
  api,
  kaira,
}: {
  plan: Plan;
  patient: Patient;
  derived: NonNullable<BuilderDerived>;
  mi: number;
  oi: number;
  targetEdit: TargetEdit | null;
  dispatch: Dispatch;
  api: Builder['api'];
  kaira: Builder['kaira'];
}) {
  const stat = derived.stats[mi] ?? derived.stats[0];
  const M = stat.m;
  const T = stat.T;
  const opts = stat.opts;
  const O = M.options[oi] ?? M.options[0];
  const safeOi = M.options.includes(O) ? oi : 0;
  const A = totals(O.foods);
  const edited = Object.keys(M.ov).length > 0;
  const ed = targetEdit?.mi === mi ? targetEdit : null;
  const optSt = opts[safeOi];
  const showKaira = T.k > 0 && !ed && optSt?.st !== 'ok';
  const kairaCacheKey = kairaKey(mi, safeOi, O.foods, T);
  const kairaRequest: KairaRequest = useMemo(() => ({
    patientId: patient.id,
    planCondition: plan.templateName || patient.program,
    mealSlot: M.key,
    mealName: M.name,
    target: T,
    current: A,
    foods: O.foods.map(f => ({ name: f.name, qty: f.qty, unit: f.unit })),
  }), [patient.id, patient.program, plan.templateName, M.key, M.name, T, A, O.foods]);

  // In-page food search row; closes when the selected meal/option changes.
  const [addingFood, setAddingFood] = useState(false);
  useEffect(() => { setAddingFood(false); }, [mi, safeOi]);

  const daily = useMemo(() => {
    const gp = gramsPerDay(plan.kcal, plan.pct, 'p');
    const gc = gramsPerDay(plan.kcal, plan.pct, 'c');
    const gf = gramsPerDay(plan.kcal, plan.pct, 'f');
    const sum = derived.active.reduce(
      (acc, st) => ({
        k: acc.k + st.T.k,
        p: acc.p + st.T.p,
        c: acc.c + st.T.c,
        f: acc.f + st.T.f,
      }),
      { k: 0, p: 0, c: 0, f: 0 },
    );
    return { goal: { k: plan.kcal, p: gp, c: gc, f: gf }, sum };
  }, [plan, derived.active]);

  const setEdit = (k: MacroKeyEval, v: string) => ed && dispatch({ type: 'targetEdit', edit: { mi, v: { ...ed.v, [k]: v } } });

  return (
    <div className={s.mealsStepPage}>
      <aside className={s.mealsSidebar}>
        <div className={s.mealsVsGoalCard}>
          <span className={s.mealsEyebrow}>{copy.meals.vsDailyGoal}</span>
          {EVAL_KEYS.map(({ k, label, unit }) => (
            <div key={k} className={s.mealsVsGoalRow}>
              <span>{label}</span>
              <span className={s.mealsVsGoalVal}>
                {k === 'k' ? fmtInt(daily.sum.k) : fmtG(daily.sum[k])} / {k === 'k' ? fmtInt(daily.goal.k) : fmtG(daily.goal[k])} {unit}
              </span>
            </div>
          ))}
        </div>
        <span className={s.mealsEyebrow} style={{ padding: '0 4px 2px' }}>{copy.meals.mealsLabel}</span>
        {derived.stats.map((st, idx) => {
          const meta = mealNavMeta(st.T, st.opts);
          const active = idx === mi;
          return (
            <button
              key={st.m.key}
              type="button"
              className={s.mealNavBtn}
              data-active={active}
              onClick={() => dispatch({ type: 'select', mi: idx, oi: 0 })}
            >
              <span className={s.mealNavDot} style={{ background: st.m.skip ? 'var(--gf-border)' : meta.dot }} aria-hidden />
              <span className={s.mealNavName}>{st.m.name}</span>
              <span className={s.mealNavKcal}>{st.m.skip ? '—' : meta.kcal}</span>
              <span
                className={s.mealNavSub}
                style={{ color: st.m.skip ? 'var(--gf-faint)' : meta.subColor }}
              >
                {st.m.time} · {st.m.skip ? copy.meals.notPart : meta.sub}
              </span>
            </button>
          );
        })}
      </aside>

      <div className={s.mealsMainCard}>
        <div className={s.mealsMainHead}>
          <div className={s.mealsMainTitle}>
            <h3>{M.name}</h3>
            <span>{M.time}</span>
          </div>
        </div>

        {M.skip ? (
          <div className={s.mealsSkipped}>{copy.meals.skipped}</div>
        ) : (
          <div className={s.mealsMainBody}>
            <div className={s.mealTargetPanel}>
              <div className={s.mealTargetPanelHead}>
                <b>{copy.meals.mealTarget}</b>
                <span className={s.mealTargetHint}>{copy.meals.toleranceHint}</span>
                <span className={s.mealTargetActions}>
                  {!ed && (
                    <>
                      <button
                        type="button"
                        className={s.btnOutlineGray}
                        title={copy.option.fromOpts}
                        onClick={() => dispatch({ type: 'targetFromOptions' })}
                      >
                        {copy.meals.setFromMedian}
                      </button>
                      <button
                        type="button"
                        className={s.btnOutlineGreen}
                        onClick={() => {
                          dispatch({ type: 'targetEdit', edit: null });
                          dispatch({ type: 'go', screen: 'targets' });
                        }}
                      >
                        <Icon.Pen size={14} />
                        {copy.option.edit}
                      </button>
                    </>
                  )}
                  {ed && (
                    <>
                      <button type="button" className={s.btnOutlineGray} onClick={() => dispatch({ type: 'targetEdit', edit: null })}>
                        {copy.option.cancel}
                      </button>
                      <button type="button" className={s.btnOutlineGreen} onClick={() => dispatch({ type: 'targetSave' })}>
                        {copy.option.save}
                      </button>
                    </>
                  )}
                </span>
              </div>
              <div className={s.mealTargetMetrics}>
                {EVAL_KEYS.map(({ k, label, unit }) => {
                  const { low, high } = band(T[k], k !== 'k');
                  if (ed) {
                    return (
                      <label key={k} className={s.mealTargetMetricBox}>
                        <span className={s.evalMetricLabel}>
                          <MetricMark k={k} />
                          {label}
                        </span>
                        <input
                          className={s.mealTargetMetricInput}
                          type="number"
                          inputMode="decimal"
                          value={ed.v[k]}
                          onChange={e => setEdit(k, e.target.value)}
                        />
                        <span className={s.mealTargetRange}>{fmtInt(low)}–{fmtInt(high)} {unit}</span>
                      </label>
                    );
                  }
                  return (
                    <div key={k} className={s.mealTargetMetricBox}>
                      <span className={s.evalMetricLabel}>
                        <MetricMark k={k} />
                        {label}
                      </span>
                      <span className={s.mealTargetMetricVal}>
                        <b>{k === 'k' ? fmtInt(T[k]) : fmtG(T[k])}</b>
                        <span>{unit}</span>
                      </span>
                      {T[k] > 0 && <span className={s.mealTargetRange}>{fmtInt(low)}–{fmtInt(high)} {unit}</span>}
                    </div>
                  );
                })}
              </div>
              {edited && !ed && <span className={s.pillBlue}>{copy.option.edited}</span>}
            </div>

            <div className={s.optionTabsRow}>
              {M.options.map((_, idx) => {
                const st = opts[idx];
                const dotOk = st?.st === 'ok';
                return (
                  <button
                    key={idx}
                    type="button"
                    className={idx === safeOi ? s.btnOutlineGreen : s.btnOutlineGray}
                    onClick={() => dispatch({ type: 'select', oi: idx })}
                  >
                    {copy.meals.option} {idx + 1}
                    <span className={s.optionTabDot} style={{ background: dotOk ? 'var(--gf-green)' : 'var(--gf-red-ink)' }} />
                  </button>
                );
              })}
              <button
                type="button"
                className={s.optionTabAdd}
                onClick={() => dispatch({ type: 'addOption', mi, openSearch: false })}
              >
                {copy.meals.addOption}
              </button>
              {M.options.length > 1 && (
                <button type="button" className={s.optionTabRemove} onClick={() => dispatch({ type: 'removeOption' })}>
                  {copy.meals.removeOption}
                </button>
              )}
            </div>

            <div className={s.evalGrid}>
              {EVAL_KEYS.map(({ k, label, unit }) => (
                <EvalMetricCard key={k} k={k} label={label} unit={unit} actual={A[k]} target={T[k]} />
              ))}
            </div>

            {/* {showKaira && (
              <KairaSuggestions
                kaira={kaira}
                cacheKey={kairaCacheKey}
                request={kairaRequest}
                mealName={M.name}
                planLabel={(plan.templateName || 'this').split(',')[0].toLowerCase()}
              />
            )} */}

            <div className={s.foodsTableWrap}>
              <div className={s.foodsTable} data-foods-table>
                <div className={s.foodsTableHead}>
                  <span>Food</span>
                  <span>Quantity</span>
                  <span>Measure</span>
                  <span>kcal</span>
                  <span>Prot g</span>
                  <span>Carb g</span>
                  <span>Fat g</span>
                  <span />
                </div>
                {O.foods.map((f, fi) => (
                  <div key={fi} className={s.foodsTableRow}>
                    <input className={s.foodsNameInput} readOnly value={f.name} placeholder="Search food" />
                    <div className={s.foodsQty}>
                      <button
                        type="button"
                        className={s.stepBtnXs}
                        aria-label="Less"
                        onClick={() => dispatch({ type: 'foodQty', fi, qty: f.qty - (f.qty > 1 ? 0.5 : 0.25) })}
                      >
                        <Icon.Minus size={14} />
                      </button>
                      <input
                        className={s.foodsQtyInput}
                        type="number"
                        step={0.5}
                        min={0}
                        value={f.qty}
                        onChange={e => dispatch({ type: 'foodQty', fi, qty: parseFloat(e.target.value) || 0.25 })}
                      />
                      <button
                        type="button"
                        className={s.stepBtnXs}
                        aria-label="More"
                        onClick={() => dispatch({ type: 'foodQty', fi, qty: f.qty + (f.qty >= 1 ? 0.5 : 0.25) })}
                      >
                        <Icon.Plus size={14} />
                      </button>
                    </div>
                    <span className={s.foodsUnit}>{f.unit}</span>
                    <span className={s.foodsNumStrong}>{fmtInt(f.per[0] * f.qty)}</span>
                    <span className={s.foodsNum}>{fmtG(f.per[1] * f.qty)}</span>
                    <span className={s.foodsNum}>{fmtG(f.per[2] * f.qty)}</span>
                    <span className={s.foodsNum}>{fmtG(f.per[3] * f.qty)}</span>
                    <TrashBtn label="Remove food" onClick={() => dispatch({ type: 'removeFood', fi })} />
                  </div>
                ))}
                {addingFood && (
                  <InlineFoodSearch
                    api={api}
                    slot={M.key}
                    onPick={food => {
                      dispatch({ type: 'addFood', food, inline: true });
                      setAddingFood(false);
                    }}
                    onClose={() => setAddingFood(false)}
                  />
                )}
                {!addingFood && (
                  <div className={s.foodsTableFoot}>
                    <button
                      type="button"
                      className={s.btnOutlineGreen}
                      onClick={() => setAddingFood(true)}
                    >
                      {copy.meals.addFood}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className={s.tipsCard}>
              <b className={s.tipsTitle}>{copy.meals.tipsTitle}</b>
              <div className={s.tipsBanner}>{copy.meals.tipsBanner}</div>
              {O.tips.map((t, ti) => (
                <div key={ti} className={s.tipRow}>
                  <span className={s.tipNum}>{ti + 1}</span>
                  <div className={s.tipFields}>
                    <input
                      className={s.tipHeadline}
                      placeholder="Headline, e.g. Soak the methi seeds overnight"
                      value={t.heading}
                      aria-describedby={t.heading.length > TIP_HEADING_MAX_LEN ? `tip-head-limit-${ti}` : undefined}
                      onChange={e => dispatch({ type: 'plan', fn: p => { p.meals[mi].options[safeOi].tips[ti].heading = e.target.value; } })}
                    />
                    {t.heading.length > TIP_HEADING_MAX_LEN && (
                      <p id={`tip-head-limit-${ti}`} className={s.tipLimitAlert} role="alert">
                        {copy.meals.tipCharOver(TIP_HEADING_MAX_LEN)}
                      </p>
                    )}
                    <input
                      className={s.tipDetail}
                      placeholder="One line on how to do it"
                      value={t.detail}
                      aria-describedby={t.detail.length > TIP_DETAIL_MAX_LEN ? `tip-detail-limit-${ti}` : undefined}
                      onChange={e => dispatch({ type: 'plan', fn: p => { p.meals[mi].options[safeOi].tips[ti].detail = e.target.value; } })}
                    />
                    {t.detail.length > TIP_DETAIL_MAX_LEN && (
                      <p id={`tip-detail-limit-${ti}`} className={s.tipLimitAlert} role="alert">
                        {copy.meals.tipCharOver(TIP_DETAIL_MAX_LEN)}
                      </p>
                    )}
                  </div>
                  <TrashBtn label="Delete tip" onClick={() => dispatch({ type: 'tip', op: 'remove', ti })} />
                </div>
              ))}
              <button type="button" className={s.btnOutlineGreen} onClick={() => dispatch({ type: 'tip', op: 'add' })}>
                {copy.meals.addTip}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
